import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  HeadingNode,
  QuoteNode,
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  HeadingTagType,
} from '@lexical/rich-text';
import { ListItemNode, ListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, $isListNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $setBlocksType } from '@lexical/selection';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import {
  $getRoot,
  $insertNodes,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  EditorState,
  TextFormatType,
} from 'lexical';
import { supabase } from '../../../lib/supabase';
import {
  ImageNode,
  $createImageNode,
  INSERT_IMAGE_COMMAND,
} from './WysiwygImageNode';

/* ── Types ── */
type BlockType = 'paragraph' | 'h1' | 'h2' | 'h3' | 'quote' | 'bullet' | 'number';
interface ToolbarState {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrikethrough: boolean;
  blockType: BlockType;
}

/* ── InitialHtml Plugin ── */
function InitialHtmlPlugin({ html }: { html: string }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      if (!html) return;
      try {
        const parser = new DOMParser();
        const dom = parser.parseFromString(html, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        root.select();
        $insertNodes(nodes);
      } catch {
        root.append($createParagraphNode());
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/* ── Toolbar State Plugin ── */
function ToolbarPlugin({ onStateChange }: { onStateChange: (s: ToolbarState) => void }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;
          const anchorNode = selection.anchor.getNode();
          const element =
            anchorNode.getKey() === 'root'
              ? anchorNode
              : anchorNode.getTopLevelElementOrThrow();
          let blockType: BlockType = 'paragraph';
          if ($isHeadingNode(element)) {
            blockType = element.getTag() as BlockType;
          } else if ($isListNode(element)) {
            const parent = $getNearestNodeOfType(anchorNode, ListNode);
            if (parent) blockType = parent.getListType() === 'bullet' ? 'bullet' : 'number';
          } else if (element.getType() === 'quote') {
            blockType = 'quote';
          }
          onStateChange({
            isBold: selection.hasFormat('bold'),
            isItalic: selection.hasFormat('italic'),
            isUnderline: selection.hasFormat('underline'),
            isStrikethrough: selection.hasFormat('strikethrough'),
            blockType,
          });
        });
      }),
    );
  }, [editor, onStateChange]);
  return null;
}

/* ── Image Insert Plugin & Modal ── */
function InsertImagePlugin({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [editor] = useLexicalComposerContext();
  const [tab, setTab] = useState<'upload' | 'url'>('upload');
  const [urlInput, setUrlInput] = useState('');
  const [altText, setAltText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return editor.registerCommand(
      INSERT_IMAGE_COMMAND,
      ({ src, altText: alt = '' }) => {
        const node = $createImageNode(src, alt);
        $insertNodes([node]);
        if (node.getNextSibling() === null) {
          const p = $createParagraphNode();
          node.insertAfter(p);
          p.select();
        }
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  const doInsert = (src: string, alt: string) => {
    if (!src) return;
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, { src, altText: alt });
    onClose();
    setUrlInput('');
    setAltText('');
    setPreviewUrl('');
    setUploadedUrl('');
    setTab('upload');
  };

  const handleFile = async (file: File) => {
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);
    setUploading(true);
    const ext = file.name.split('.').pop();
    const path = `news-content/${Date.now()}.${ext}`;
    const { data, error } = await supabase.storage
      .from('service-images')
      .upload(path, file, { upsert: true });
    setUploading(false);
    if (error) {
      alert('업로드 실패: ' + error.message);
      return;
    }
    const { data: { publicUrl } } = supabase.storage.from('service-images').getPublicUrl(data.path);
    setUploadedUrl(publicUrl);
  };

  const handleClose = () => {
    onClose();
    setUrlInput('');
    setAltText('');
    setPreviewUrl('');
    setUploadedUrl('');
    setTab('upload');
  };

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <h4 className="font-serif text-navy font-bold text-base">이미지 삽입</h4>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-navy/50" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Tabs */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            {(['upload', 'url'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`flex-1 py-2 text-xs font-sans font-semibold rounded-md cursor-pointer transition-all whitespace-nowrap ${
                  tab === t ? 'bg-white text-navy' : 'text-navy/40 hover:text-navy/60'
                }`}
              >
                {t === 'upload' ? <><i className="ri-upload-cloud-line mr-1" />파일 업로드</> : <><i className="ri-link mr-1" />URL 입력</>}
              </button>
            ))}
          </div>

          {/* Upload Tab */}
          {tab === 'upload' && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                  e.target.value = '';
                }}
              />
              {previewUrl ? (
                <div className="relative w-full h-40 rounded-xl overflow-hidden bg-[#F8F9FB]">
                  <img src={previewUrl} alt="preview" className="w-full h-full object-contain" />
                  {uploading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin text-2xl text-navy/50" />
                      <span className="ml-2 text-sm text-navy/50 font-sans">업로드 중...</span>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  onClick={() => fileRef.current?.click()}
                  className="w-full h-36 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-navy/40 hover:bg-[#F8F9FB] transition-all"
                >
                  <i className="ri-image-add-line text-2xl text-navy/25 mb-2" />
                  <p className="text-sm text-navy/35 font-sans">클릭하여 이미지 선택</p>
                  <p className="text-xs text-navy/25 font-sans mt-0.5">JPG, PNG, WEBP, GIF</p>
                </div>
              )}

              {/* Alt text */}
              <div>
                <label className="block text-xs font-sans text-navy/40 mb-1.5">이미지 설명 <span className="text-navy/25">(선택)</span></label>
                <input
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                  placeholder="이미지에 대한 간단한 설명"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy"
                />
              </div>

              {/* Insert button — shows after upload completes */}
              {uploadedUrl && !uploading && (
                <button
                  type="button"
                  onClick={() => doInsert(uploadedUrl, altText)}
                  className="w-full bg-navy text-white text-sm font-sans font-semibold py-3 rounded-lg cursor-pointer hover:bg-navy/90 transition-colors whitespace-nowrap"
                >
                  <i className="ri-image-line mr-1.5" />본문에 삽입
                </button>
              )}
            </>
          )}

          {/* URL Tab */}
          {tab === 'url' && (
            <>
              {urlInput && (
                <div className="w-full h-36 rounded-xl overflow-hidden bg-[#F8F9FB]">
                  <img src={urlInput} alt="preview" className="w-full h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
              )}
              <input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy"
              />

              {/* Alt text */}
              <div>
                <label className="block text-xs font-sans text-navy/40 mb-1.5">이미지 설명 <span className="text-navy/25">(선택)</span></label>
                <input
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                  placeholder="이미지에 대한 간단한 설명"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy"
                />
              </div>

              <button
                type="button"
                onClick={() => { if (urlInput) doInsert(urlInput, altText); }}
                disabled={!urlInput}
                className="w-full bg-navy text-white text-sm font-sans font-semibold py-3 rounded-lg disabled:opacity-40 cursor-pointer hover:bg-navy/90 transition-colors whitespace-nowrap"
              >
                <i className="ri-image-line mr-1.5" />본문에 삽입
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

/* ── Toolbar UI ── */
interface ToolbarUIProps {
  state: ToolbarState;
  onInsertImage: () => void;
}

function ToolbarUI({ state, onInsertImage }: ToolbarUIProps) {
  const [editor] = useLexicalComposerContext();

  const formatText = (format: TextFormatType) => editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);

  const setBlock = (type: BlockType) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      if (type === 'bullet') { editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined); return; }
      if (type === 'number') { editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined); return; }
      $setBlocksType(selection, () => {
        if (type === 'paragraph') return $createParagraphNode();
        if (type === 'quote') return $createQuoteNode();
        return $createHeadingNode(type as HeadingTagType);
      });
    });
  };

  const btn = 'flex items-center justify-center w-8 h-8 rounded-md text-sm transition-colors cursor-pointer';
  const on = 'bg-navy text-white';
  const off = 'text-navy/50 hover:bg-gray-100 hover:text-navy';
  const blk = (t: BlockType) => state.blockType === t ? on : off;
  const div = <div className="w-px h-5 bg-gray-200 mx-0.5" />;

  return (
    <div className="flex items-center flex-wrap gap-0.5 px-3 py-2 border-b border-gray-200 bg-[#F8F9FB] rounded-t-xl">
      <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(UNDO_COMMAND, undefined); }} className={`${btn} ${off}`} title="실행 취소"><i className="ri-arrow-go-back-line" /></button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.dispatchCommand(REDO_COMMAND, undefined); }} className={`${btn} ${off}`} title="다시 실행"><i className="ri-arrow-go-forward-line" /></button>
      {div}
      <button type="button" onMouseDown={(e) => { e.preventDefault(); setBlock('paragraph'); }} className={`${btn} ${blk('paragraph')}`} title="본문"><i className="ri-paragraph" /></button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); setBlock('h1'); }} className={`${btn} px-2 text-xs font-bold ${blk('h1')}`}>H1</button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); setBlock('h2'); }} className={`${btn} px-2 text-xs font-bold ${blk('h2')}`}>H2</button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); setBlock('h3'); }} className={`${btn} px-2 text-xs font-bold ${blk('h3')}`}>H3</button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); setBlock('quote'); }} className={`${btn} ${blk('quote')}`} title="인용"><i className="ri-double-quotes-l" /></button>
      {div}
      <button type="button" onMouseDown={(e) => { e.preventDefault(); setBlock('bullet'); }} className={`${btn} ${blk('bullet')}`} title="글머리"><i className="ri-list-unordered" /></button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); setBlock('number'); }} className={`${btn} ${blk('number')}`} title="번호 목록"><i className="ri-list-ordered" /></button>
      {div}
      <button type="button" onMouseDown={(e) => { e.preventDefault(); formatText('bold'); }} className={`${btn} font-bold ${state.isBold ? on : off}`} title="굵게">B</button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); formatText('italic'); }} className={`${btn} italic ${state.isItalic ? on : off}`} title="기울임">I</button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); formatText('underline'); }} className={`${btn} underline ${state.isUnderline ? on : off}`} title="밑줄">U</button>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); formatText('strikethrough'); }} className={`${btn} line-through ${state.isStrikethrough ? on : off}`} title="취소선">S</button>
      {div}
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); onInsertImage(); }}
        className={`${btn} ${off}`}
        title="이미지 삽입"
      >
        <i className="ri-image-add-line" />
      </button>
    </div>
  );
}

/* ── HTML Output Plugin ── */
function HtmlOutputPlugin({ onChange }: { onChange: (html: string) => void }) {
  const [editor] = useLexicalComposerContext();
  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor, null);
        onChange(html);
      });
    },
    [editor, onChange],
  );
  return <OnChangePlugin onChange={handleChange} />;
}

/* ── Main Export ── */
export interface WysiwygEditorProps {
  value: string;
  onChange: (html: string) => void;
  editorKey?: string;
  placeholder?: string;
  minHeight?: number;
}

const editorNodes = [
  HeadingNode, QuoteNode, ListNode, ListItemNode,
  LinkNode, AutoLinkNode, ImageNode,
];

const editorTheme = {
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'line-through',
    underlineStrikethrough: 'underline line-through',
  },
  heading: { h1: 'wysiwyg-h1', h2: 'wysiwyg-h2', h3: 'wysiwyg-h3' },
  list: { ul: 'wysiwyg-ul', ol: 'wysiwyg-ol', listitem: 'wysiwyg-li' },
  quote: 'wysiwyg-quote',
  paragraph: 'wysiwyg-p',
};

export default function WysiwygEditor({
  value,
  onChange,
  editorKey,
  placeholder = '본문 내용을 입력하세요...',
  minHeight = 280,
}: WysiwygEditorProps) {
  const [toolbarState, setToolbarState] = useState<ToolbarState>({
    isBold: false, isItalic: false, isUnderline: false,
    isStrikethrough: false, blockType: 'paragraph',
  });
  const [showImageModal, setShowImageModal] = useState(false);

  const initialConfig = {
    namespace: `WysiwygEditor-${editorKey || 'default'}`,
    theme: editorTheme,
    onError: (err: Error) => console.error('[Lexical]', err),
    nodes: editorNodes,
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden focus-within:border-navy transition-colors bg-white">
      <LexicalComposer key={editorKey} initialConfig={initialConfig}>
        <ToolbarPlugin onStateChange={setToolbarState} />
        <ToolbarUI state={toolbarState} onInsertImage={() => setShowImageModal(true)} />

        <div className="relative" style={{ minHeight }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="wysiwyg-content outline-none px-5 py-4 text-sm text-navy/80 font-sans leading-relaxed overflow-auto"
                style={{ minHeight }}
              />
            }
            placeholder={
              <div className="absolute top-4 left-5 text-sm text-navy/25 pointer-events-none font-sans select-none">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>

        <InitialHtmlPlugin html={value} />
        <HtmlOutputPlugin onChange={onChange} />
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <InsertImagePlugin
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
        />
      </LexicalComposer>

      <style>{`
        .wysiwyg-content { display: block; }
        .wysiwyg-p { margin-bottom: 0.75rem; }
        .wysiwyg-h1 { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.75rem; color: #1a2035; font-family: serif; }
        .wysiwyg-h2 { font-size: 1.25rem; font-weight: 700; margin-bottom: 0.625rem; color: #1a2035; font-family: serif; }
        .wysiwyg-h3 { font-size: 1.1rem; font-weight: 600; margin-bottom: 0.5rem; color: #1a2035; }
        .wysiwyg-ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 0.75rem; }
        .wysiwyg-ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 0.75rem; }
        .wysiwyg-li { margin-bottom: 0.25rem; }
        .wysiwyg-quote { border-left: 3px solid #c8a97a; padding-left: 1rem; color: rgba(26,32,53,0.6); font-style: italic; margin-bottom: 0.75rem; }
      `}</style>
    </div>
  );
}
